import SVG from 'svg.js';
import { Geometry } from './canvasModel';
import { translateFromSVG, translateToSVG } from './shared';

type ActivateShortcutKeys = 'altKey' | 'shiftKey' | 'ctrlKey';

const CONFIG = {
    EDGE_MAGNET: {
        MOUSE_MOVE_DEBOUNCE_TIMEOUT: 5,
        MIN_DISTANCE: 100,
        EDGE_MAGNET_CROSSHAIR: {
            SIZE: 100,
            STROKE_WIDTH: 1,
            STROKE_COLOR: '#ff0000',
        },
    },
};

type Point = { x:number, y:number };
interface LineSegment {
    start: Point;
    end: Point;
}
type ShapesProvider = () => SVG.Shape[];
type DrawingAnnotationStateProvider = () => boolean;

type EdgeMagnetCrosshairElements = {
    group: SVG.G,
    line1: SVG.Line,
    line2: SVG.Line,
};

type EdgeMagnetMouseEvent = {
    readonly event: MouseEvent,
    readonly positionChange: boolean,
};

export interface EdgeMagnetHandler {
    transform(geometry: Geometry): void;
    setSourceMouseEvent(e: MouseEvent): EdgeMagnetHandler;
    getEdgeMagnetMouseEvent(eventType?: string): EdgeMagnetMouseEvent;
    crosshairCoordinate(e: MouseEvent, point: number[]): number[]
    hideEdgeMagnetCrosshair(): EdgeMagnetHandler
    release(): void;
    edgeMagnetEnabled(flag?:boolean):boolean
}

type MinDistanceData = {
    readonly distance: number
    readonly snapPoint: Point,
    readonly shape: SVG.Shape
};

export class EdgeMagnetHandlerImpl implements EdgeMagnetHandler {
    private readonly isDrawingAnnotation: DrawingAnnotationStateProvider;
    private readonly canvas: SVG.Container;
    private geometry: Geometry;
    private mouseMoveDebounceTimeout:number | null;
    private readonly shapesProvider:ShapesProvider;
    private edgeMagnetCrosshair:EdgeMagnetCrosshairElements;
    private activateShortcutKeys:ActivateShortcutKeys[] = ['shiftKey', 'ctrlKey'];
    private sourceMouseEvent: MouseEvent | null;
    private edgeMagnetMouseEvent: EdgeMagnetMouseEvent | null;

    private enabledEdgeMagnet = false;

    constructor(
        canvas: SVG.Container,
        geometry: Geometry,
        shapesProvider:ShapesProvider,
        drawingAnnotationStateProvider: DrawingAnnotationStateProvider,
    ) {
        this.canvas = canvas;
        this.geometry = geometry;
        this.shapesProvider = shapesProvider;
        this.isDrawingAnnotation = drawingAnnotationStateProvider;
        this.sourceMouseEvent = null;
        this.edgeMagnetMouseEvent = null;

        this.edgeMagnetCrosshair = this.createCrosshair(
            this.canvas.nested(),
            0,
            0,
            CONFIG.EDGE_MAGNET.EDGE_MAGNET_CROSSHAIR.SIZE,
            CONFIG.EDGE_MAGNET.EDGE_MAGNET_CROSSHAIR.STROKE_WIDTH,
            CONFIG.EDGE_MAGNET.EDGE_MAGNET_CROSSHAIR.STROKE_COLOR,
        );

        this.canvas.on('mousemove.edgeMagnet', this.handleMouseMove.bind(this));
    }

    public edgeMagnetEnabled(flag?: boolean): boolean {
        if (flag !== undefined) {
            this.enabledEdgeMagnet = flag;
        }
        return this.enabledEdgeMagnet;
    }

    public setSourceMouseEvent(e: MouseEvent): EdgeMagnetHandler {
        this.sourceMouseEvent = e;
        this.edgeMagnetMouseEvent = null;
        return this;
    }

    public getEdgeMagnetMouseEvent(eventType?: string): EdgeMagnetMouseEvent {
        if (this.edgeMagnetMouseEvent === null ||
            (eventType !== undefined && this.edgeMagnetMouseEvent.event.type !== eventType)) {
            this.edgeMagnetMouseEvent = this.createEdgeMagnetMouseEvent();
        }

        return this.edgeMagnetMouseEvent;
    }

    public transform(geometry: Geometry): void {
        this.geometry = geometry;
        this.transformEdgeMagnetCrosshair();
    }

    release(): void {

    }

    public crosshairCoordinate(e: MouseEvent, point: number[]): number[] {
        if (point.length !== 2) {
            throw new Error('Point must have two coordinates');
        }
        if (!this.isDrawingAnnotation()) {
            return point;
        }

        if (this.snappingModifiersPressed(e) === false) {
            return point;
        }
        const minDistanceToShapes = this.getMinDistanceToShapes({ x: point[0], y: point[1] });

        if (!this.isNearby(minDistanceToShapes)) {
            return point;
        }

        return [minDistanceToShapes.snapPoint.x, minDistanceToShapes.snapPoint.y];
    }

    private createEdgeMagnetMouseEvent(eventType?: string): EdgeMagnetMouseEvent {
        if (this.sourceMouseEvent === null) {
            throw new Error('Error create proxy event. Source event is not set');
        }

        let { clientX } = this.sourceMouseEvent;
        let { clientY } = this.sourceMouseEvent;
        let positionChange = false;

        const minDistanceItem = this.getMinDistanceFromCursorToShapes(this.sourceMouseEvent);
        if (this.isNearby(minDistanceItem)) {
            const [x, y] = translateFromSVG(
                (this.canvas.node as any) as SVGSVGElement,
                [minDistanceItem.snapPoint.x, minDistanceItem.snapPoint.y],
            );

            clientX = x;
            clientY = y;
            positionChange = true;
        }

        const type = eventType !== undefined ? eventType : this.sourceMouseEvent.type;

        const snappedMouseEvent = new MouseEvent(type);
        Object.defineProperties(snappedMouseEvent, {
            clientX: { value: clientX, writable: false },
            clientY: { value: clientY, writable: false },
        });

        return {
            event: snappedMouseEvent,
            positionChange,
        };
    }

    private isNearby(minDistance: MinDistanceData | undefined):boolean {
        return minDistance !== undefined && minDistance.distance < CONFIG.EDGE_MAGNET.MIN_DISTANCE;
    }

    private transformEdgeMagnetCrosshair() {
        this.edgeMagnetCrosshair.line1.attr({
            'stroke-width': CONFIG.EDGE_MAGNET.EDGE_MAGNET_CROSSHAIR.STROKE_WIDTH / (2 * this.geometry.scale),
        });
        this.edgeMagnetCrosshair.line2.attr({
            'stroke-width': CONFIG.EDGE_MAGNET.EDGE_MAGNET_CROSSHAIR.STROKE_WIDTH / (2 * this.geometry.scale),
        });
    }

    private snappingModifiersPressed(e: MouseEvent):boolean {
        let snappingModifiersPressed = true;
        for (const keyName of this.activateShortcutKeys) {
            if (e[keyName] === false) {
                snappingModifiersPressed = false;
            }
            if (snappingModifiersPressed === false) {
                break;
            }
        }

        return snappingModifiersPressed;
    }

    private handleMouseMove(e: MouseEvent): void {
        if (!this.enabledEdgeMagnet) {
            return;
        }
        if (this.mouseMoveDebounceTimeout !== null) {
            window.clearTimeout(this.mouseMoveDebounceTimeout);
        }

        this.mouseMoveDebounceTimeout = window.setTimeout(() => {
            if (!this.isDrawingAnnotation()) {
                return;
            }

            if (!this.snappingModifiersPressed(e)) {
                this.hideEdgeMagnetCrosshair();
                return;
            }

            const minDistanceItem = this.getMinDistanceFromCursorToShapes(e);
            if (this.isNearby(minDistanceItem)) {
                this.moveEdgeMagnetCrosshair(minDistanceItem.snapPoint.x, minDistanceItem.snapPoint.y);
                this.showEdgeMagnetCrosshair();
            } else {
                this.hideEdgeMagnetCrosshair();
            }
        }, CONFIG.EDGE_MAGNET.MOUSE_MOVE_DEBOUNCE_TIMEOUT);
    }

    private getMinDistanceFromCursorToShapes(e: MouseEvent):MinDistanceData | undefined {
        const [svgX, svgY] = translateToSVG((this.canvas.node as any) as SVGSVGElement, [e.clientX, e.clientY]);
        return this.getMinDistanceToShapes({ x: svgX, y: svgY });
    }

    private getMinDistanceToShapes(point:Point):MinDistanceData | undefined {
        const shapes = this.shapesProvider();
        let currentMinDistance = Infinity;
        let minDistanceItem:MinDistanceData | undefined;

        for (const shape of shapes) {
            const minDistance = this.getMinDistance(shape, { x: point.x, y: point.y });
            if (minDistance !== undefined && minDistance.distance < currentMinDistance) {
                currentMinDistance = minDistance.distance;
                minDistanceItem = minDistance;
            }
        }

        return minDistanceItem;
    }

    private getMinDistance(shape: SVG.Shape, point: Point): MinDistanceData | undefined {
        let minDistance;

        // const supportedShapes = [
        //     'rectangle', 'polygon', 'polyline', 'points', 'ellipse', 'cuboid', 'skeleton', 'mask',
        // ];
        switch (shape.type) {
            case 'rect':
                minDistance = this.getMinDistanceToRectangle(shape, point);
                break;
            default:
                console.warn('It is impossible to determine the minimum distance. Unsupported shape type:', shape.type);
        }

        return minDistance;
    }

    private getMinDistanceToRectangle(shape: SVG.Shape, point: Point):MinDistanceData {
        const box = shape.bbox();
        const originalPoints = [
            { x: box.x, y: box.y }, // Верхний левый угол
            { x: box.x + box.width, y: box.y }, // Верхний правый угол
            { x: box.x + box.width, y: box.y + box.height }, // Нижний правый угол
            { x: box.x, y: box.y + box.height }, // Нижний левый угол
        ];

        const { matrix } = shape.transform();
        const transformedPoints = originalPoints.map((originalPoint) => ({
            x: matrix.a * originalPoint.x + matrix.c * originalPoint.y + matrix.e,
            y: matrix.b * originalPoint.x + matrix.d * originalPoint.y + matrix.f,
        }));

        let minDistance = Infinity;
        let pointOnLine:Point | undefined;
        const lineSegments:LineSegment[] = [
            {
                start: { x: transformedPoints[0].x, y: transformedPoints[0].y },
                end: { x: transformedPoints[1].x, y: transformedPoints[1].y },
            },
            {
                start: { x: transformedPoints[1].x, y: transformedPoints[1].y },
                end: { x: transformedPoints[2].x, y: transformedPoints[2].y },
            },
            {
                start: { x: transformedPoints[2].x, y: transformedPoints[2].y },
                end: { x: transformedPoints[3].x, y: transformedPoints[3].y },
            },
            {
                start: { x: transformedPoints[3].x, y: transformedPoints[3].y },
                end: { x: transformedPoints[0].x, y: transformedPoints[0].y },
            },
        ];
        for (const lineSegment of lineSegments) {
            const distanceItem = this.distanceFromPointToLineSegment(point, lineSegment);
            if (distanceItem.distance < minDistance) {
                minDistance = distanceItem.distance;
                pointOnLine = distanceItem.snapPoint;
            }
        }

        return {
            distance: minDistance,
            shape,
            snapPoint: pointOnLine,
        };
    }

    private distanceFromPointToLineSegment(point: Point, segment: LineSegment): { distance: number, snapPoint: Point } {
        const { start, end } = segment;

        const dx = end.x - start.x;
        const dy = end.y - start.y;

        const segmentLengthSquared = dx * dx + dy * dy;

        if (segmentLengthSquared === 0) {
            return {
                distance: this.distanceBetweenPoints(point, start),
                snapPoint: start,
            };
        }

        const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / segmentLengthSquared;

        if (t < 0) {
            return {
                distance: this.distanceBetweenPoints(point, start),
                snapPoint: start,
            };
        }

        if (t > 1) {
            return {
                distance: this.distanceBetweenPoints(point, end),
                snapPoint: end,
            };
        }

        const closestPoint: Point = {
            x: start.x + t * dx,
            y: start.y + t * dy,
        };

        return {
            distance: this.distanceBetweenPoints(point, closestPoint),
            snapPoint: closestPoint,
        };
    }

    private distanceBetweenPoints(p1: Point, p2: Point): number {
        return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
    }

    private createCrosshair(
        svgContainer: SVG.Container,
        x: number,
        y: number,
        size: number,
        strokeWidth: number,
        strokeColor: string,
    ): EdgeMagnetCrosshairElements {
        // Создаем группу для перекрестия
        const crosshairGroup = svgContainer.group();
        crosshairGroup.hide();

        // Вычисляем половину размера для удобства
        const halfSize = size / 2;

        const stroke = strokeWidth / this.geometry.scale;

        // Создаем линию "\" (сверху-слева вниз-направо)
        const line1 = crosshairGroup.line(x - halfSize, y - halfSize, x + halfSize, y + halfSize)
            .stroke({ width: stroke, color: strokeColor })
            .attr({ 'shape-rendering': 'crispEdges' });

        // Создаем линию "/" (снизу-слева вверх-направо)
        const line2 = crosshairGroup.line(x - halfSize, y + halfSize, x + halfSize, y - halfSize)
            .stroke({ width: stroke, color: strokeColor })
            .attr({ 'shape-rendering': 'crispEdges' });

        return {
            group: crosshairGroup,
            line1,
            line2,
        };
    }

    private moveEdgeMagnetCrosshair(x: number, y: number): void {
        const halfSize = CONFIG.EDGE_MAGNET.EDGE_MAGNET_CROSSHAIR.SIZE / 2;

        this.edgeMagnetCrosshair.line1.plot(x - halfSize, y - halfSize, x + halfSize, y + halfSize);
        this.edgeMagnetCrosshair.line2.plot(x - halfSize, y + halfSize, x + halfSize, y - halfSize);
    }

    private showEdgeMagnetCrosshair(): void {
        this.edgeMagnetCrosshair.group.show();
    }

    public hideEdgeMagnetCrosshair(): EdgeMagnetHandler {
        this.edgeMagnetCrosshair.group.hide();

        return this;
    }
}
